<template>
  <div class="pa-2 space-y-2.5">
    <v-toolbar color="primary" dark>
      Sales Report
    </v-toolbar>
    <v-card class="space-y-2.5 pa-2">
      <v-btn v-if="$route.meta.backLink" color="error" @click="$router.replace($route.meta.backLink)">
        Cancel
      </v-btn>
      <v-select v-model="includedHeaders" hide-details="auto" deletable-chips dense item-color="primary" small-chips
                outlined :items="headers" item-text="text" multiple item-value="value" label="Column Filter"
                @change="onIncludeHeaders" />
      <v-text-field dense outlined label="Date" hide-details="auto" aria-label="" type="date"
                    :value="salesInsight.date" @change="onInsightDateChange" />
      <div>
        <v-checkbox v-model="onlyNegatives" hide-details="auto" label="Only Negatives" dense />
      </div>
      <div class="action-btns">
        <v-btn color="primary" @click="loadData">
          Load Data
        </v-btn>
        <v-btn color="success" @click="printInsight">
          Print Insight
        </v-btn>
        <v-btn color="success" @click="exportCSV">
          Export CSV
        </v-btn>
      </div>
    </v-card>
    <v-card style="max-height: 1080px; overflow: auto;">
      <v-simple-table>
        <template #default>
          <thead>
            <tr>
              <th v-for="header in filterHeader" :key="header.value">
                <div class="header" @click="sortHeader(header.value)">
                  <span>{{ header.text }}</span>
                  <v-icon :class="getHeaderArrowClass(header.value)">
                    mdi-arrow-up
                  </v-icon>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in tableItems" :key="index">
              <td v-for="(header) in filterHeader" :key="header.value">
                <span :style="item.style">{{ item[header.value] }}</span>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card>
  </div>
</template>
<style scoped>
.action-btns {
  display: flex;
  gap: 10px;
  align-items: center;
}

.header {
  display: flex;
  gap: 5px;
  cursor: pointer;
  align-items: center;
}

.header .v-icon {
  transition-duration: 500ms;
}

.header .v-icon.transparent {
  opacity: 0;
}

.header .v-icon.desc {
  transform: rotate(180deg);
}
</style>
<script>
import { ErrorPopup, LoadingPopup, getCurrentDateEnUsFormat, toPercent, formatAccounting } from '@/lib/utils'
import { isAxiosError } from 'axios'
import LocalStore from '../../../../lib/localStore'
import { mapState } from 'vuex'
import appAxios from '../../../../extra-functions/axios.extra'
import { getCurrentDate, orderBy } from '../../../../lib/utils'
import SaleDailyInsight from '../../../../pdf/sale-daily-insight.pdf'


const localStorage = new LocalStore()

export default {
  name: 'PrintoutsSalesReport',
  data() {
    return {
      headers: [],
      includedHeaders: localStorage.get('salesReport.insight.included', []),
      salesInsight: {
        date: localStorage.get('salesReport.insight.date', getCurrentDate()),
        items: [],
        details: {},
      },
      onlyNegatives: false,
      sortBy: new Map([]),
    }
  },
  computed: {
    filterHeader() {
      const included = new Set([...this.includedHeaders])
      const result = this.headers
        .filter(header => included.has(header.value))
      return result
    },
    tableItems() {
      const result = this.salesInsight.items.filter(item => {
        if (this.onlyNegatives) return item.negative
        return true
      })
      /** @type {Map} */
      const sortBy = this.sortBy
      return orderBy(result, [...sortBy.keys()], [...sortBy.values()])
    },
    ...mapState({
      storeId: state => state.settings.storeId,
      store: state => state.branchLocation.list.find(location => location.id === state.settings.storeId),
      token: state => state.auth.token,
    }),
  },
  created() {
    this.getDailySaleInsight()
  },
  methods: {
    async getDailySaleInsight() {
      try {
        LoadingPopup.fire()
        const axios = appAxios(this.token)
        const { data } = await axios.get('/stores/sales-daily-report', {
          params: {
            date: this.salesInsight.date,
          },
        })
        const { headers, items, ...details } = data.original
        this.headers = headers
        this.salesInsight = {
          ...this.salesInsight,
          details,
          items: items.map(item => {
            return {
              ...item,
              retail: formatAccounting(item.retail),
              amount: formatAccounting(item.amount),
              cost: formatAccounting(item.cost),
              profit: formatAccounting(item.profit),
              discount: formatAccounting(item.discount),
            }
          }),
        }
        LoadingPopup.close()
      } catch (e) {
        LoadingPopup.close()
        if (isAxiosError(e)) {
          const { status, data: { errors } } = e?.response
          if (status === 422) {
            LoadingPopup.close()
            this.errors = errors
          }
        }
        console.error(e)
        ErrorPopup.fire()
      }
    },
    formatAccounting,
    onIncludeHeaders(val = []) {
      const localSalesReport = localStorage.get('salesReport')
      localStorage.set({
        salesReport: {
          ...localSalesReport,
          insight: {
            ...localSalesReport?.insight,
            included: val,
          },
        },
      })
    },
    onInsightModeChange(val) {
      if (val) {
        const insight = localStorage.get('salesReport.insight', {
          included: [],
          date: getCurrentDate(),
        })
        this.includedHeaders = insight.included
        this.salesInsight.date = insight.date
        return
      }
      this.includedHeaders = localStorage.get('salesReport.default.included', [])
    },
    onInsightDateChange(date) {
      this.salesInsight = { ...this.salesInsight, date }
      const salesReport = localStorage.get('salesReport', {})
      localStorage.set({
        salesReport: {
          ...salesReport,
          insight: {
            ...salesReport.insight,
            date,
          },
        },
      })
    },
    loadData() {
      this.getDailySaleInsight()
    },
    getHeaderArrowClass(value) {
      if (!this.sortBy.has(value)) return 'transparent'
      return this.sortBy.get(value) === 'desc' ? 'desc' : ''
    },
    sortHeader(value) {
      /** @type {Map} */
      const sortBy = this.sortBy
      if (sortBy.has(value)) {
        if (sortBy.get(value) === 'desc') {
          sortBy.delete(value)
          this.sortBy = new Map([...sortBy])
          return
        }
        sortBy.set(value, 'desc')
        this.sortBy = new Map([...sortBy])
        return
      }
      sortBy.set(value, 'asc')
      this.sortBy = new Map([...sortBy])
    },
    exportCSV() {
      const headers = this.filterHeader.map(header => header.text)
      const rows = this.tableItems.map(item => {
        return this.filterHeader.map(header => item[header.value])
      })

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(value => `"${value}"`).join(',')),
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      const storeName = `${this.store.name}_daily_sales_insight_${new Date().getTime()}.csv`
      link.setAttribute('download',
        storeName.replace(/[\s]+/g, '_').toLocaleLowerCase())
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    printInsight() {
      const headers = this.filterHeader.map(item => item.text)
      const headerSet = new Set(this.filterHeader.map(h => h.value))
      const accounting = new Set([
        'retail',
        'amount',
        'cost',
        'profit',
        'discount',
      ])
      const pdf = new SaleDailyInsight()
      const body = this.tableItems.map(item => {
        const keys = [...headerSet]
        return keys.reduce((prev, key) => {
          if (key === 'item' && item.pdfStyle) {
            const col = {
              content: `${item.item} (${item.brandType})`,
              styles: item.pdfStyle,
            }
            prev.push(col)
            return prev
          }
          if (accounting.has(key)) {
            const col = {
              content: item[key],
              styles: { halign: 'right' },
            }
            prev.push(col)
            return prev
          }
          prev.push(item[key])
          return prev
        }, [])
      })
      const { details } = this.salesInsight
      const brandTypes = Object.keys(details.brandTypes)
      pdf.generate({
        headers,
        storeName: this.store.name,
        dateRange: this.salesInsight.date,
        revenue: formatAccounting(details.revenue),
        totalCost: formatAccounting(details.totalCost),
        grossProfit: formatAccounting(details.grossProfit),
        brandTypeAmounts: brandTypes.reduce((prev, type) => {
          prev[type] = formatAccounting(details.brandTypes[type])
          return prev
        }, {}),
      }, body)
    },
  },
}
</script>
